import { useCallback, useEffect, useState } from 'react';
import { FileText, Loader2, Trash2 } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { cn } from '../lib/cn';
import { MailingIntestazione } from '../components/email/MailingIntestazione';
import { EmailPreview } from '../components/email/EmailPreview';
import { useNotifications } from '../store/useStore';
import {
  AMBITI,
  templateService,
  type AmbitoModello,
  type EmailTemplate,
} from '../lib/email/templateService';

function usato(modello: EmailTemplate): string {
  if (modello.usi_count === 0) return 'mai usato';
  return modello.usi_count === 1 ? 'usato una volta' : `usato ${modello.usi_count} volte`;
}

export default function EmailTemplatesPage() {
  const { addNotification } = useNotifications();

  const [modelli, setModelli] = useState<EmailTemplate[]>([]);
  const [sceltoId, setSceltoId] = useState<string | null>(null);
  const [caricando, setCaricando] = useState(true);

  const carica = useCallback(async () => {
    try {
      const elenco = await templateService.elenco();
      setModelli(elenco);
      setSceltoId((precedente) => precedente ?? elenco[0]?.id ?? null);
    } catch (errore) {
      addNotification({
        type: 'error',
        title: 'Non riesco a leggere i modelli',
        message: errore instanceof Error ? errore.message : 'Errore imprevisto.',
      });
    } finally {
      setCaricando(false);
    }
  }, [addNotification]);

  useEffect(() => {
    carica();
  }, [carica]);

  const scelto = modelli.find((m) => m.id === sceltoId) ?? null;

  /** Le modifiche si scrivono quando si esce dal campo, non a ogni tasto. */
  const salva = async (id: string, campi: Partial<EmailTemplate>) => {
    try {
      await templateService.aggiorna(id, campi as never);
    } catch (errore) {
      addNotification({
        type: 'error',
        title: 'Non riesco a salvare',
        message: errore instanceof Error ? errore.message : 'Errore imprevisto.',
      });
    }
  };

  const elimina = async (modello: EmailTemplate) => {
    await templateService.elimina(modello.id);
    setModelli((m) => m.filter((x) => x.id !== modello.id));
    setSceltoId(null);
    addNotification({
      type: 'info',
      title: 'Modello eliminato',
      // Vale la pena dirlo: elimina il modello, non quello che ci hai fatto.
      message: 'Le email gia\' partite e le campagne nate da lui restano come sono.',
    });
  };

  return (
    <div className="space-y-4 md:space-y-6">
      <MailingIntestazione descrizione="Email messe da parte per riusarle. Si salvano dalle campagne, dalle sequenze o scrivendo a un lead." />

      {caricando ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-6 h-6 animate-spin text-neutral-400" />
        </div>
      ) : modelli.length === 0 ? (
        <Card className="border-gray-200 border-dashed">
          <CardContent className="py-14 text-center">
            <FileText className="w-10 h-10 text-neutral-400 mx-auto mb-3 opacity-30" />
            <p className="text-sm text-neutral-600 font-medium">Nessun modello</p>
            <p className="text-xs text-neutral-400 mt-1 max-w-md mx-auto leading-relaxed">
              Componi una email in una campagna o in una sequenza e premi "Salva come modello":
              la ritrovi qui, pronta da riusare.
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4 lg:grid-cols-[280px_minmax(0,1fr)]">
          <div className="space-y-1.5">
            {modelli.map((modello) => (
              <button
                key={modello.id}
                type="button"
                onClick={() => setSceltoId(modello.id)}
                className={cn(
                  'w-full text-left px-3 py-2.5 rounded-lg border bg-white transition-colors',
                  sceltoId === modello.id
                    ? 'border-primary-400 bg-primary-50/50'
                    : 'border-gray-200 hover:border-gray-300'
                )}
              >
                <span className="block text-xs font-semibold text-gray-900 truncate">
                  {modello.name}
                </span>
                <span className="block text-[11px] text-neutral-500 truncate mt-0.5">
                  {modello.subject || 'senza oggetto'}
                </span>
                <span className="block text-[11px] text-neutral-400 mt-1">
                  {AMBITI[modello.ambito].label} · {usato(modello)}
                </span>
              </button>
            ))}
          </div>

          {scelto && (
            <Card className="border-gray-200">
              <CardContent className="p-4 space-y-4">
                <div className="grid gap-3 sm:grid-cols-2">
                  <div>
                    <Label htmlFor="nome" className="text-xs">
                      Nome
                    </Label>
                    <Input
                      id="nome"
                      defaultValue={scelto.name}
                      key={`nome-${scelto.id}`}
                      onBlur={(e) => {
                        const name = e.target.value.trim();
                        if (!name || name === scelto.name) return;
                        setModelli((m) =>
                          m.map((x) => (x.id === scelto.id ? { ...x, name } : x))
                        );
                        salva(scelto.id, { name });
                      }}
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Dove viene proposto</Label>
                    <select
                      value={scelto.ambito}
                      onChange={(e) => {
                        const ambito = e.target.value as AmbitoModello;
                        setModelli((m) =>
                          m.map((x) => (x.id === scelto.id ? { ...x, ambito } : x))
                        );
                        salva(scelto.id, { ambito });
                      }}
                      className="w-full mt-1 rounded-md border border-gray-200 px-2 py-2 text-sm"
                    >
                      {(Object.keys(AMBITI) as AmbitoModello[]).map((a) => (
                        <option key={a} value={a}>
                          {AMBITI[a].label}
                        </option>
                      ))}
                    </select>
                    <p className="text-[11px] text-neutral-400 mt-1 leading-relaxed">
                      {AMBITI[scelto.ambito].spiegazione}
                    </p>
                  </div>
                </div>

                {scelto.description && (
                  <p className="text-xs text-neutral-500 leading-relaxed">{scelto.description}</p>
                )}

                <EmailPreview
                  documento={scelto.document}
                  oggetto={scelto.subject}
                  preheader={scelto.preheader ?? ''}
                />

                <div className="flex justify-between items-center pt-3 border-t border-gray-100">
                  <p className="text-[11px] text-neutral-400">
                    Il contenuto si modifica ripartendo da qui in una campagna e salvandolo di
                    nuovo: cosi' quello che e' gia' partito non cambia.
                  </p>
                  <Button
                    variant="outline"
                    onClick={() => elimina(scelto)}
                    className="text-red-600 border-red-200 hover:bg-red-50 flex-shrink-0"
                  >
                    <Trash2 className="w-4 h-4 mr-2" />
                    Elimina
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      )}
    </div>
  );
}
